// components/MoleculeViewer.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Info, Trash2, Rocket, FileJson, Edit2, X, Check } from 'lucide-react';
import { supabase } from '../../../shared/lib/supabase';
import { toast } from 'sonner';
import { useTabNavigation } from '../../../shared/hooks/useTabNavigation';
import { useTabManager } from '../../../shared/contexts/TabContext';
import { detectFileFormat, parseCIFFile } from '../lib/molecularParser';
import { Molecule3DViewer } from './Molecule3DViewer';
import InputMasker from '../../jobs/components/InputMasker';

interface ProteinDesignInputProps {
  moleculeId: string;
  onDelete?: () => Promise<void>;
  fileName?: string;
  initialRunName?: string;
  initialMaskData?: {
    activeMaskTypes: string[];
    maskedResidues: { [key: string]: string[] };
  };
}

interface ResidueInfo {
  chain: string;
  resNum: number;
  resName: string;
  index: number;
}

interface SequenceData {
  chain: string;
  sequence: string;
  residues: ResidueInfo[];
}

type MaskType = 'sequence' | 'structure' | 'sasa' | 'plddt';

interface MaskColors {
  sequence: string;
  structure: string;
  sasa: string;
  plddt: string;
}

const MASK_COLORS: MaskColors = {
  sequence: '#3B82F6', // blue
  structure: '#EF4444', // red
  sasa: '#10B981', // green
  plddt: '#FACC15', // yellow
};

export function ProteinDesignInput({ moleculeId, onDelete, fileName, initialRunName, initialMaskData }: ProteinDesignInputProps) {
  const { openOdysseyTab } = useTabNavigation();
  const runOdysseyButtonRef = useRef<HTMLButtonElement>(null);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [sequences, setSequences] = useState<SequenceData[]>([]);
  const [moleculeData, setMoleculeData] = useState<any>(null);
  const [previousGenerationName, setPreviousGenerationName] = useState<string | null>(null);
  const [previousGenIndexInfo, setPreviousGenIndexInfo] = useState<{ current: number; total: number } | null>(null);
  const [pdbContent, setPdbContent] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [showAddDataModal, setShowAddDataModal] = useState(false);
  const [odysseyLoading, setOdysseyLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [additionalDataOptions, setAdditionalDataOptions] = useState({
    sasa: false,
    semanticDescription: false,
    orthologousGroups: false,
    domains: false
  });
  const [sasaInput, setSasaInput] = useState('');
  const [showSasaInfo, setShowSasaInfo] = useState(false);
  const [orthologousInput, setOrthologousInput] = useState('');
  const [showOrthologousInfo, setShowOrthologousInfo] = useState(false);
  const [domainsInput, setDomainsInput] = useState('');
  const [showDomainsInfo, setShowDomainsInfo] = useState(false);
  const [semanticInput, setSemanticInput] = useState('');
  const [showSemanticInfo, setShowSemanticInfo] = useState(false);
  const [manualMaskChain, setManualMaskChain] = useState('');
  const [manualMaskRange, setManualMaskRange] = useState('');
  const [manualMaskError, setManualMaskError] = useState(false);
  const [notes, setNotes] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [originalNotes, setOriginalNotes] = useState('');
  // Sequence interaction moved into InputMasker component
  // Spinning animation now handled by Molecule3DViewer
  
  // Track which data types have been uploaded at least once
  const [hasUploadedSasa, setHasUploadedSasa] = useState(false);
  const [hasUploadedSemantic, setHasUploadedSemantic] = useState(false);
  const [hasUploadedOrthologous, setHasUploadedOrthologous] = useState(false);
  const [hasUploadedDomains, setHasUploadedDomains] = useState(false);
  
  // New state for multi-mask system - initialize from props if available
  const [activeMaskTypes, setActiveMaskTypes] = useState<Set<MaskType>>(() => {
    if (initialMaskData?.activeMaskTypes) {
      return new Set(initialMaskData.activeMaskTypes as MaskType[]);
    }
    return new Set(['sequence']);
  });
  
  const [maskedResidues, setMaskedResidues] = useState<Map<MaskType, Set<string>>>(() => {
    if (initialMaskData?.maskedResidues) {
      const map = new Map<MaskType, Set<string>>();
      Object.entries(initialMaskData.maskedResidues).forEach(([maskType, residues]) => {
        map.set(maskType as MaskType, new Set(residues));
      });
      return map;
    }
    return new Map([
      ['sequence', new Set()],
      ['structure', new Set()],
      ['sasa', new Set()],
      ['plddt', new Set()]
    ]);
  });
  // Drag snapshot moved into InputMasker
  
  // State for run name modal
  const [showNameModal, setShowNameModal] = useState(!initialRunName);
  const [runName, setRunName] = useState(initialRunName || '');
  const [isNameSet, setIsNameSet] = useState(!!initialRunName);
  const [numGens, setNumGens] = useState(1);
  
  // Get tab management functions
  const { closeTab, activeTabId } = useTabManager();

  // Calculate total number of residues across all chains
  const totalResidues = sequences.reduce((total, seq) => total + seq.residues.length, 0);

  useEffect(() => {
    loadMolecule();
    // Legacy 3D cleanup removed - now handled by Molecule3DViewer
  }, [moleculeId]);

  // Legacy spinning animation useEffect removed - now handled by Molecule3DViewer

  const saveMoleculeName = async () => {
    if (!runName.trim() || !moleculeId) return;
    
    try {
      const { error } = await supabase
        .from('molecule_viewer')
        .update({ name: runName.trim() })
        .eq('id', moleculeId);
      
      if (error) throw error;
      
      // Update local state to reflect the saved name
      if (moleculeData) {
        setMoleculeData({ ...moleculeData, name: runName.trim() });
      }
    } catch (error) {
      console.error('Error saving molecule name:', error);
      toast.error('Failed to save molecule name');
    }
  };

  const saveNotes = async () => {
    try {
      const { error } = await supabase
        .from('molecule_viewer')
        .update({ notes: notes.trim() })
        .eq('id', moleculeId);
      
      if (error) throw error;
      
      // Update local state
      if (moleculeData) {
        setMoleculeData({ ...moleculeData, notes: notes.trim() });
      }
      setOriginalNotes(notes.trim());
      setIsEditingNotes(false);
      toast.success('Notes saved');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    }
  };

  const cancelNotesEdit = () => {
    setNotes(originalNotes);
    setIsEditingNotes(false);
  };

  const loadMolecule = async () => {
    try {
      // Fetch molecule data from Supabase
      const { data: molecule, error } = await supabase
        .from('molecule_viewer')
        .select('*')
        .eq('id', moleculeId)
        .single();
  
      if (error) throw error;
      
      // Check if this molecule has already been run through Odyssey
      if (molecule?.ran_odyssey) {
        setIsReadOnly(true);
        // Clear all active mask types for read-only mode
        setActiveMaskTypes(new Set());
        
        // Load additional data
        if (molecule.sasa) setSasaInput(molecule.sasa);
        if (molecule.semantic_description) setSemanticInput(molecule.semantic_description);
        if (molecule.orthologous_groups) setOrthologousInput(molecule.orthologous_groups);
        if (molecule.domains) setDomainsInput(molecule.domains);
        
        // Set which additional data options were used
        setAdditionalDataOptions({
          sasa: !!molecule.sasa,
          semanticDescription: !!molecule.semantic_description,
          orthologousGroups: !!molecule.orthologous_groups,
          domains: !!molecule.domains
        });
      }
      
      // Check if storage_path exists before trying to download
      if (!molecule?.storage_path) {
        throw new Error('Invalid molecule data: missing storage path');
      }
      
      setMoleculeData(molecule);

      // If this molecule was created from an Odyssey job output, read index/total from metadata
      try {
        const meta: any = molecule?.metadata || null;
        const prevInfo = meta?.previous_generation_info;
        if (prevInfo && typeof prevInfo.total_outputs === 'number' && prevInfo.total_outputs > 1) {
          const idx = typeof prevInfo.output_index === 'number' ? prevInfo.output_index + 1 : null;
          if (idx) setPreviousGenIndexInfo({ current: idx, total: prevInfo.total_outputs });
        }
      } catch (e) {
        // Best effort only
      }
      
      // Always initialize notes from database (clear if no notes)
      setNotes(molecule.notes || '');
      setOriginalNotes(molecule.notes || '');
      
      // If molecule has a name and we don't have initialRunName, use it
      if (molecule.name && !initialRunName) {
        setRunName(molecule.name);
        setIsNameSet(true);
        setShowNameModal(false);
      }
      
      // Fetch previous generation name if it exists
      if (molecule.previous_generation) {
        const { data: previousGen } = await supabase
          .from('molecule_viewer')
          .select('name')
          .eq('id', molecule.previous_generation)
          .single();
        
        if (previousGen?.name) {
          setPreviousGenerationName(previousGen.name);
        }
      }
  
      // Fetch the actual PDB file
      const { data: fileData, error: downloadError } = await supabase.storage
        .from('molecule-files')
        .download(molecule.storage_path);
  
      if (downloadError) throw downloadError;
  
      const content = await fileData.text();
      setPdbContent(content);
      
      // Parse sequences from structure file (PDB or CIF)
      const parsedSequences = parseStructureSequences(content);
      setSequences(parsedSequences);
      
      // Initialize chain selector with first chain
      if (parsedSequences.length > 0) {
        setManualMaskChain(parsedSequences[0].chain);
      }
      
      // Load saved mask data AFTER sequences are loaded (for read-only mode)
      if (molecule?.ran_odyssey) {
        // Load saved mask data if available - create a new Map to trigger React update
        const newMaskedResidues = new Map<MaskType, Set<string>>();
        
        if (molecule.sequence_masks) {
          const savedSequenceMasks = JSON.parse(molecule.sequence_masks);
          newMaskedResidues.set('sequence', new Set(savedSequenceMasks));
        }
        if (molecule.structure_masks) {
          const savedStructureMasks = JSON.parse(molecule.structure_masks);
          newMaskedResidues.set('structure', new Set(savedStructureMasks));
        }
        if (molecule.sasa_masks) {
          const savedSasaMasks = JSON.parse(molecule.sasa_masks);
          newMaskedResidues.set('sasa', new Set(savedSasaMasks));
        }
        if (molecule.plddt_masks) {
          const savedPlddtMasks = JSON.parse(molecule.plddt_masks);
          newMaskedResidues.set('plddt', new Set(savedPlddtMasks));
        }
        
        // Set the new Map to trigger React state update and useEffect
        setMaskedResidues(newMaskedResidues);
      }
    } catch (error) {
      console.error('Error loading molecule:', error);
      toast.error('Failed to load molecular structure');
      // Optionally notify parent component that loading failed
      if (onDelete) {
        // You might want to add an onLoadError prop instead
      }
    } finally {
      setLoading(false);
    }
  };

  const parseStructureSequences = (content: string): SequenceData[] => {
    try {
      const format = detectFileFormat(content);
      
      if (format === 'pdb') {
        return parsePDBSequences(content);
      } else if (format === 'cif') {
        return parseCIFSequences(content);
      } else {
        // Fallback to PDB parsing
        return parsePDBSequences(content);
      }
    } catch (error) {
      console.error('Error parsing structure sequences:', error);
      // Fallback to PDB parsing
      return parsePDBSequences(content);
    }
  };

  const parsePDBSequences = (pdbContent: string): SequenceData[] => {
    const lines = pdbContent.split('\n');
    const sequenceMap = new Map<string, ResidueInfo[]>();
    
    // Common HETATM entries to skip (water, ions, ligands)
    const skipResidues = new Set(['HOH', 'WAT', 'H2O', 'DOD', 'NA', 'CL', 'K', 'CA', 'MG', 'ZN', 'FE', 'MN', 'SO4', 'PO4']);
    
    // Parse ATOM records to get actual sequence
    lines.forEach(line => {
      if (line.startsWith('ATOM') || line.startsWith('HETATM')) {
        const chain = line.substring(21, 22).trim() || 'A';
        const resNum = parseInt(line.substring(22, 26).trim());
        const resName = line.substring(17, 20).trim();
        
        // Skip water molecules and common ions
        if (resName && skipResidues.has(resName.toUpperCase())) {
          return;
        }
        
        if (!sequenceMap.has(chain)) {
          sequenceMap.set(chain, []);
        }
        
        const chainResidues = sequenceMap.get(chain)!;
        // Add residue if not already present (avoid duplicates)
        if (!chainResidues.some(r => r.resNum === resNum)) {
          chainResidues.push({
            chain,
            resNum,
            resName: convertToSingleLetter(resName),
            index: chainResidues.length
          });
        }
      }
    });

    // Convert to SequenceData array
    const sequences: SequenceData[] = [];
    sequenceMap.forEach((residues, chain) => {
      // Sort by residue number
      residues.sort((a, b) => a.resNum - b.resNum);
      sequences.push({
        chain,
        sequence: residues.map(r => r.resName).join(''),
        residues
      });
    });

    return sequences;
  };

  const parseCIFSequences = (cifContent: string): SequenceData[] => {
    try {
      // Use the molecular parser to get CIF atoms
      const parsedData = parseCIFFile(cifContent, 'atomic');
      const sequenceMap = new Map<string, ResidueInfo[]>();
      
      // Common HETATM entries to skip (water, ions, ligands)
      const skipResidues = new Set(['HOH', 'WAT', 'H2O', 'DOD', 'NA', 'CL', 'K', 'CA', 'MG', 'ZN', 'FE', 'MN', 'SO4', 'PO4']);
      
      // Extract sequence data from parsed atomic data
      // The molecular parser returns table data, we need to reconstruct atom info
      parsedData.rows.forEach(row => {
        // Table columns: 'Serial', 'Element', 'Atom Name', 'X', 'Y', 'Z', 'Residue', 'Res#', 'Chain', 'Occupancy', 'B-Factor'
        const resName = row[6]; // Residue column
        const resNum = parseInt(row[7]); // Res# column  
        const chain = row[8] || 'A'; // Chain column
        
        // Skip water molecules and common ions
        if (resName && skipResidues.has(resName.toUpperCase())) {
          return;
        }
        
        if (!sequenceMap.has(chain)) {
          sequenceMap.set(chain, []);
        }
        
        const chainResidues = sequenceMap.get(chain)!;
        // Add residue if not already present (avoid duplicates)
        if (!chainResidues.some(r => r.resNum === resNum)) {
          chainResidues.push({
            chain,
            resNum,
            resName: convertToSingleLetter(resName),
            index: chainResidues.length
          });
        }
      });

      // Convert to SequenceData array
      const sequences: SequenceData[] = [];
      sequenceMap.forEach((residues, chain) => {
        // Sort by residue number
        residues.sort((a, b) => a.resNum - b.resNum);
        sequences.push({
          chain,
          sequence: residues.map(r => r.resName).join(''),
          residues
        });
      });

      return sequences;
    } catch (error) {
      console.error('Error parsing CIF sequences:', error);
      return [];
    }
  };

  // Define amino acid atoms structure (matching Python script)
  const BACKBONE = ['N', 'CA', 'C', 'CB'];
  const ATOMS: { [key: string]: string[] } = {
    'V': [...BACKBONE, 'O', 'CG1', 'CG2'],
    'R': [...BACKBONE, 'O', 'CG', 'CD', 'NE', 'CZ', 'NH1', 'NH2'],
    'C': [...BACKBONE, 'O', 'SG'],
    'S': [...BACKBONE, 'O', 'OG'],
    'L': [...BACKBONE, 'O', 'CG', 'CD1', 'CD2'],
    'Q': [...BACKBONE, 'O', 'CG', 'CD', 'OE1', 'NE2'],
    'E': [...BACKBONE, 'O', 'CG', 'CD', 'OE1', 'OE2'],
    'T': [...BACKBONE, 'O', 'OG1', 'CG2'],
    'A': [...BACKBONE, 'O'],
    'D': [...BACKBONE, 'O', 'CG', 'OD1', 'OD2'],
    'G': [...BACKBONE, 'O'], // CB is the phantom side-chain
    'H': [...BACKBONE, 'O', 'CG', 'ND1', 'CD2', 'CE1', 'NE2'],
    'I': [...BACKBONE, 'O', 'CG1', 'CG2', 'CD1'],
    'P': [...BACKBONE, 'O', 'CG', 'CD'],
    'F': [...BACKBONE, 'O', 'CG', 'CD1', 'CD2', 'CE1', 'CE2', 'CZ'],
    'K': [...BACKBONE, 'O', 'CG', 'CD', 'CE', 'NZ'],
    'N': [...BACKBONE, 'O', 'CG', 'OD1', 'ND2'],
    'M': [...BACKBONE, 'O', 'CG', 'SD', 'CE'],
    'Y': [...BACKBONE, 'O', 'CG', 'CD1', 'CD2', 'CE1', 'CE2', 'CZ', 'OH'],
    'W': [...BACKBONE, 'O', 'CG', 'CD1', 'CD2', 'NE1', 'CE2', 'CE3', 'CZ2', 'CZ3', 'CH2'],
    'X': [...BACKBONE, 'O'], // Unknown amino acid
    'U': [...BACKBONE, 'O', 'SE'], // Selenocysteine
    'Z': [...BACKBONE, 'O', 'CG', 'CD', 'OE1', 'OE2'], // Glutamic acid or Glutamine
    'B': [...BACKBONE, 'O', 'CG', 'OD1', 'ND2'], // Asparagine or Aspartic acid
    'O': [...BACKBONE, 'O', 'CG', 'CD', 'CE', 'NZ'], // Ornithine
    'J': [...BACKBONE, 'O', 'CG', 'CD1', 'CD2'] // Leucine or Isoleucine
  };

  const convertToSingleLetter = (threeLetter: string): string => {
    // Handle null/undefined input
    if (!threeLetter || typeof threeLetter !== 'string') {
      return 'X'; // Return unknown amino acid for invalid input
    }

    const mapping: { [key: string]: string } = {
      // Standard amino acids
      'ALA': 'A', 'CYS': 'C', 'ASP': 'D', 'GLU': 'E', 'PHE': 'F',
      'GLY': 'G', 'HIS': 'H', 'ILE': 'I', 'LYS': 'K', 'LEU': 'L',
      'MET': 'M', 'ASN': 'N', 'PRO': 'P', 'GLN': 'Q', 'ARG': 'R',
      'SER': 'S', 'THR': 'T', 'VAL': 'V', 'TRP': 'W', 'TYR': 'Y',
      // Modified/Non-standard amino acids often found in PDB files
      'MSE': 'M', // Selenomethionine (often used in crystallography)
      'SEC': 'U', // Selenocysteine
      'PYL': 'O', // Pyrrolysine
      'ASX': 'B', // Aspartic acid or Asparagine
      'GLX': 'Z', // Glutamic acid or Glutamine
      'XLE': 'J', // Leucine or Isoleucine
      'UNK': 'X', // Unknown
      // Common modifications
      'PTR': 'Y', // Phosphotyrosine
      'SEP': 'S', // Phosphoserine
      'TPO': 'T', // Phosphothreonine
      'HYP': 'P', // Hydroxyproline
      'MLY': 'K', // Methylated lysine
      'CSO': 'C', // S-hydroxycysteine
      'CSD': 'C', // S-cysteinesulfinic acid
    };
    
    const result = mapping[threeLetter.toUpperCase()];
    // Silently handle unknown amino acids - they'll be represented as 'X'
    return result || 'X'; // X represents unknown/non-standard amino acids
  };

  // Build backbone coordinates - Used for JSON download functionality only (backend uses gemmi)
  const buildBackboneCoordinates = (pdbContent: string, sequences: SequenceData[]): { N: number[][], CA: number[][], C: number[][] } => {
    const lines = pdbContent.split('\n');
    const atomData: { [key: string]: { [atomName: string]: number[] } } = {};
    
    // First pass: collect all atom coordinates
    lines.forEach(line => {
      if (line.startsWith('ATOM') || line.startsWith('HETATM')) {
        const atomName = line.substring(12, 16).trim();
        const chain = line.substring(21, 22).trim() || 'A';
        const resNum = parseInt(line.substring(22, 26).trim());
        const x = parseFloat(line.substring(30, 38).trim());
        const y = parseFloat(line.substring(38, 46).trim());
        const z = parseFloat(line.substring(46, 54).trim());
        
        const key = `${chain}-${resNum}`;
        if (!atomData[key]) {
          atomData[key] = {};
        }
        atomData[key][atomName] = [x, y, z];
      }
    });
    
    // Second pass: build backbone coordinates (N, CA, C only)
    const backboneCoords = { N: [] as number[][], CA: [] as number[][], C: [] as number[][] };
    
    sequences.forEach(seq => {
      seq.residues.forEach(residue => {
        const key = `${residue.chain}-${residue.resNum}`;
        const residueAtoms = atomData[key];
        
        // Add backbone atoms (N, CA, C) in order - always use actual coordinates
        for (const atomName of ['N', 'CA', 'C']) {
          if (residueAtoms && residueAtoms[atomName]) {
            backboneCoords[atomName as keyof typeof backboneCoords].push(residueAtoms[atomName]);
          } else {
            // If backbone atom is missing, use [-1, -1, -1]
            backboneCoords[atomName as keyof typeof backboneCoords].push([-1, -1, -1]);
          }
        }
      });
    });
    
    return backboneCoords;
  };

  // Parse atoms from PDB - Used for JSON download functionality only (backend uses gemmi)
  const parseAtomsFromPDB = (pdbContent: string, sequences: SequenceData[]): { 
    atom_names: string[], 
    atom_coordinates: number[][],
    residue_ids: string[],
    residue_names: string[]
  } => {
    const lines = pdbContent.split('\n');
    const atomData: { [key: string]: { [atomName: string]: number[] } } = {};
    
    // First pass: collect all atom coordinates
    lines.forEach(line => {
      if (line.startsWith('ATOM') || line.startsWith('HETATM')) {
        const atomName = line.substring(12, 16).trim();
        const chain = line.substring(21, 22).trim() || 'A';
        const resNum = parseInt(line.substring(22, 26).trim());
        const x = parseFloat(line.substring(30, 38).trim());
        const y = parseFloat(line.substring(38, 46).trim());
        const z = parseFloat(line.substring(46, 54).trim());
        
        const key = `${chain}-${resNum}`;
        if (!atomData[key]) {
          atomData[key] = {};
        }
        atomData[key][atomName] = [x, y, z];
      }
    });
    
    // Three-letter to one-letter mapping
    const THREE_TO_ONE: { [key: string]: string } = {
      'ALA': 'A', 'ARG': 'R', 'ASN': 'N', 'ASP': 'D', 'CYS': 'C',
      'GLN': 'Q', 'GLU': 'E', 'GLY': 'G', 'HIS': 'H', 'ILE': 'I',
      'LEU': 'L', 'LYS': 'K', 'MET': 'M', 'PHE': 'F', 'PRO': 'P',
      'SER': 'S', 'THR': 'T', 'TRP': 'W', 'TYR': 'Y', 'VAL': 'V',
      'SEC': 'U', 'PYL': 'O', 'ASX': 'B', 'GLX': 'Z', 'XLE': 'J',
      'UNK': 'X'
    };
    
    // Get three-letter residue names from PDB
    const residueThreeLetter: { [key: string]: string } = {};
    lines.forEach(line => {
      if (line.startsWith('ATOM') || line.startsWith('HETATM')) {
        const chain = line.substring(21, 22).trim() || 'A';
        const resNum = parseInt(line.substring(22, 26).trim());
        const resName = line.substring(17, 20).trim();
        const key = `${chain}-${resNum}`;
        if (!residueThreeLetter[key]) {
          residueThreeLetter[key] = resName;
        }
      }
    });
    
    // Second pass: build ordered atom lists
    const atom_names: string[] = [];
    const atom_coordinates: number[][] = [];
    const residue_ids: string[] = [];
    const residue_names: string[] = [];
    
    sequences.forEach(seq => {
      seq.residues.forEach(residue => {
        const key = `${residue.chain}-${residue.resNum}`;
        const residueAtoms = atomData[key];
        const residueId = `${residue.chain}${residue.resNum}`;
        const threeLetter = residueThreeLetter[key] || 'UNK';
        const oneLetter = THREE_TO_ONE[threeLetter] || 'X';
        
        if (residueAtoms) {
          // Get expected atoms for this amino acid type
          const expectedAtoms = ATOMS[oneLetter] || ATOMS['X'];
          
          // Add atoms in the expected order
          expectedAtoms.forEach(atomName => {
            // Skip CB atoms for Glycine residues (Gly should only have N, CA, C, O)
            if (oneLetter === 'G' && atomName === 'CB') {
              return; // Skip this atom
            }
            
            if (residueAtoms[atomName]) {
              atom_names.push(atomName);
              atom_coordinates.push(residueAtoms[atomName]);
              residue_ids.push(residueId);
              residue_names.push(threeLetter);
            }
          });
        }
      });
    });
    
    return { atom_names, atom_coordinates, residue_ids, residue_names };
  };

  // Legacy 3D highlighting function removed - now handled by Molecule3DViewer

  // Legacy 3D initialization removed - now handled by Molecule3DViewer

  // Legacy 3D resize observer removed - now handled by Molecule3DViewer

  // Legacy initialize3Dmol function removed - now handled by Molecule3DViewer

  // Store reference to masks for use in 3Dmol callbacks
  // Masking logic handled within InputMasker

  // Sequence drag and hover are managed by InputMasker

  // Legacy 3D control functions removed - now handled by Molecule3DViewer

  const handleManualMaskAdd = (chain: string, range: string, maskType: string) => {
    // Parse input - can be single number or range
    let start: number, end: number;
    if (range.includes('-')) {
      // It's a range
      const parts = range.split('-').map(Number);
      start = parts[0];
      end = parts[1];
      if (start > end) {
        toast.error('Start position must be less than end position');
        return;
      }
    } else {
      // It's a single number
      start = end = Number(range);
    }

    // Find the chain data
    const chainData = sequences.find(seq => seq.chain === chain);
    if (!chainData) {
      toast.error('Chain not found');
      return;
    }

    // Validate position is within chain bounds
    if (start < 1 || end > chainData.residues.length) {
      toast.error(`Position must be between 1 and ${chainData.residues.length} for chain ${chain}`);
      return;
    }

    // Apply masks to the range
    const newMasks = new Map<MaskType, Set<string>>();
    maskedResidues.forEach((residues, maskType) => {
      newMasks.set(maskType, new Set(residues));
    });

    // Process each residue in the range
    const allMaskTypes: MaskType[] = ['sequence', 'structure', 'sasa', 'plddt'];
    for (let position = start; position <= end; position++) {
      // Use position index (1-based) instead of PDB residue number
      const residue = chainData.residues[position - 1]; // Convert to 0-based index
      if (residue) {
        const key = `${residue.chain}-${residue.resNum}`; // Use actual PDB resNum for the key

        // First, remove this residue from ALL mask types
        allMaskTypes.forEach(maskType => {
          const maskSet = newMasks.get(maskType) || new Set();
          maskSet.delete(key);
          newMasks.set(maskType, maskSet);
        });

        // Then add ONLY for active mask types
        activeMaskTypes.forEach(maskType => {
          const maskSet = newMasks.get(maskType) || new Set();
          maskSet.add(key);
          newMasks.set(maskType, maskSet);
        });
      }
    }

    setMaskedResidues(newMasks);
    toast.success(`Applied ${Array.from(activeMaskTypes).join(', ')} mask to positions ${start}${end > start ? `-${end}` : ''} in chain ${chain}`);
  };

  // Function to generate JSON data for debugging
  const generateJSON = () => {
    // Calculate total residues for SASA array
    const totalResidues = sequences.reduce((total, seq) => total + seq.residues.length, 0);
    
    // Parse SASA input - default to -1.0 when no value supplied
    let sasaArray: number[] = [];
    for (let i = 0; i < totalResidues; i++) {
      sasaArray.push(-1.0);
    }
    
    if (sasaInput.trim()) {
      const sasaValues = sasaInput.split(',').map((v: string) => parseFloat(v.trim()));
      
      // Get SASA masked residue positions
      const sasaMaskedSet = maskedResidues.get('sasa') || new Set();
      
      // Build SASA array with proper indexing
      let globalIndex = 0;
      sequences.forEach(seq => {
        seq.residues.forEach(residue => {
          const key = `${residue.chain}-${residue.resNum}`;
          
          // If this residue is SASA-masked, keep it as -1
          if (sasaMaskedSet.has(key)) {
            sasaArray[globalIndex] = -1.0;
          } else if (globalIndex < sasaValues.length && !isNaN(sasaValues[globalIndex])) {
            // Use the provided value if available and valid
            sasaArray[globalIndex] = sasaValues[globalIndex];
          }
          // Otherwise keeps default of -1.0
          
          globalIndex++;
        });
      });
    }
    
    // Build full sequence string with sequence masks applied
    const sequenceMaskedSet = maskedResidues.get('sequence') || new Set();
    let maskedSequence = '';
    sequences.forEach(seq => {
      seq.residues.forEach(residue => {
        const key = `${residue.chain}-${residue.resNum}`;
        if (sequenceMaskedSet.has(key)) {
          maskedSequence += '_';
        } else {
          maskedSequence += residue.resName;
        }
      });
    });
    
    // Parse orthologous groups input - just use the input directly as comma-separated string
    let orthologousGroupsString = "";
    if (orthologousInput.trim()) {
      // Split by comma, trim each value, filter out empty/null values, then rejoin
      const ogValues = orthologousInput
        .split(',')
        .map((v: string) => v.trim())
        .filter((v: string) => v && v.toUpperCase() !== 'NULL');
      orthologousGroupsString = ogValues.join(',');
    }
    
    // Parse domains input
    let domainsArray: (string[] | null)[] = [];
    for (let i = 0; i < totalResidues; i++) {
      domainsArray.push([]);
    }
    
    if (domainsInput.trim()) {
      const domainValues = domainsInput.split(',').map((v: string) => v.trim());
      
      for (let i = 0; i < Math.min(domainValues.length, totalResidues); i++) {
        const domainValue = domainValues[i];
        if (domainValue && domainValue.toUpperCase() !== 'NULL') {
          domainsArray[i] = [domainValue];
        }
      }
    }
    
    // Get structure masks for building structure string
    const structureMaskedSet = maskedResidues.get('structure') || new Set();
    
    // Build structure string where '_' represents masked structure positions
    let structureString = '';
    sequences.forEach(seq => {
      seq.residues.forEach(residue => {
        const key = `${residue.chain}-${residue.resNum}`;
        if (structureMaskedSet.has(key)) {
          structureString += '_';
        } else {
          // Use 'X' for unmasked positions (representing that structure is present)
          structureString += 'X';
        }
      });
    });
    
    // Parse atoms from PDB content (no coordinate masking - use actual coordinates)
    const atomsData = parseAtomsFromPDB(pdbContent, sequences);
    
    // Build backbone coordinates (no coordinate masking - use actual coordinates)
    const backboneCoords = buildBackboneCoordinates(pdbContent, sequences);
    
    // Get chain IDs
    const chainIds = sequences.map(seq => seq.chain);
    const primaryChain = chainIds[0] || 'A';
    
    // Generate IDs based on molecule data
    const moleculeId = moleculeData?.id || 'unknown';
    const fileName = moleculeData?.file_name || 'structure';
    const baseId = fileName.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9]/g, '-') || 'structure';
    
    // Create JSON object in model input format
    const jsonData = {
      sequence_id: `${baseId}-${primaryChain}`,
      pdb_id: baseId,
      chain_id: primaryChain,
      structure_id: `${baseId}-${primaryChain}-model`,
      uniprot_id: "",
      file_path: moleculeData?.file_url || "",
      is_structure: "yes",
      structure_source: "PDB",
      chains: chainIds,
      sequence: maskedSequence,
      structure: structureString,
      backbone_coordinates: backboneCoords,
      all_atoms: {
        atom_names: atomsData.atom_names,
        atom_coordinates: atomsData.atom_coordinates,
        residue_ids: atomsData.residue_ids,
        residue_names: atomsData.residue_names
      },
      plddt: new Array(totalResidues).fill(1.0),
      mean_plddt: 1.0, // Since all plddt values are 1.0, mean is 1.0
      sasa: sasaArray,
      dssp: [],
      semantic_description: semanticInput.trim(),
      orthologous_groups: orthologousGroupsString,
      domains: domainsArray
    };
    
    return jsonData;
  };

  const handleDownloadInputJson = () => {
    try {
      const jsonData = generateJSON();
      const jsonString = JSON.stringify(jsonData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const baseName =
        runName.trim() ||
        moleculeData?.name ||
        moleculeData?.file_name?.replace(/\.[^/.]+$/, '') ||
        'odyssey-input';

      const safeName = baseName.replace(/\s+/g, '-').toLowerCase();

      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeName}-input.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Input JSON downloaded');
    } catch (error) {
      console.error('Error creating input JSON download:', error);
      toast.error('Failed to create input JSON');
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Loading molecular structure...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Name Modal */}
      {showNameModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            // Close modal when clicking backdrop
            if (e.target === e.currentTarget) {
              setIsNameSet(true);
              setShowNameModal(false);
              // Use default name if none provided
              if (!runName.trim()) {
                setRunName(`Odyssey Run ${new Date().toLocaleDateString()}`);
              }
            }
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking modal content
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Name Your Odyssey Run</h2>
            <p className="text-sm text-gray-600 mb-4">
              Enter a name for this Odyssey run to help you identify it later (optional).
            </p>
            <input
              type="text"
              value={runName}
              onChange={(e) => setRunName(e.target.value.slice(0, 30))}
              maxLength={30}
              placeholder="e.g., Enzyme Design v1"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent ${
                runName.length === 30
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-purple-500'
              }`}
              autoFocus
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  setIsNameSet(true);
                  setShowNameModal(false);
                  if (!runName.trim()) {
                    setRunName(`Odyssey Run ${new Date().toLocaleDateString()}`);
                  } else {
                    await saveMoleculeName();
                  }
                } else if (e.key === 'Escape') {
                  setIsNameSet(true);
                  setShowNameModal(false);
                  if (!runName.trim()) {
                    setRunName(`Odyssey Run ${new Date().toLocaleDateString()}`);
                  }
                }
              }}
            />
            {runName.length === 30 && (
              <p className="text-xs text-red-500 mt-1">Maximum 30 characters</p>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  // Skip naming - use default
                  setIsNameSet(true);
                  setShowNameModal(false);
                  if (!runName.trim()) {
                    setRunName(`Odyssey Run ${new Date().toLocaleDateString()}`);
                  }
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition-colors"
              >
                Skip
              </button>
              <button
                onClick={async () => {
                  setIsNameSet(true);
                  setShowNameModal(false);
                  if (!runName.trim()) {
                    setRunName(`Odyssey Run ${new Date().toLocaleDateString()}`);
                  } else {
                    await saveMoleculeName();
                  }
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 font-medium transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Sequence styles now live inside InputMasker */}
      <div className="flex flex-col min-h-screen bg-white">

      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        {/* Odyssey Header */}
        <div className="mb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 mr-4">
              <input
                type="text"
                value={runName}
                onChange={(e) => setRunName(e.target.value.slice(0, 30))}
                maxLength={30}
                className="text-4xl font-light text-gray-900 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-purple-500 focus:outline-none px-1 w-full max-w-4xl"
                placeholder="Enter Name Here"
                disabled={!isNameSet}
              />
            </div>
            {onDelete && !isReadOnly && (
              <button
                onClick={onDelete}
                className="px-3 py-1.5 text-sm border border-red-300 rounded-sm text-red-600 bg-white hover:bg-red-50 transition-colors flex items-center gap-2 mt-2"
                title={`Delete ${fileName || 'file'}`}
              >
                <Trash2 className="h-4 w-4" />
                Delete File From Database
              </button>
            )}
          </div>
          <p className="text-gray-600 mt-1">
            {previousGenerationName ? (
              <>
                Previous generation: <span className="font-semibold">{previousGenerationName}</span>
                {previousGenIndexInfo && (
                  <span className="ml-2 text-gray-500">(gen {previousGenIndexInfo.current} / {previousGenIndexInfo.total})</span>
                )}
              </>
            ) : (
              'This json creator allows you to upload a PDB file and create a JSON that Odyssey can use by manually masking certain residues.'
            )}
          </p>

          {/* Notes Section */}
          <div className="mt-4">
            {isEditingNotes && !isReadOnly ? (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm"
                  placeholder="Add notes about this molecule configuration..."
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={saveNotes}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
                  >
                    <Check className="w-3 h-3" />
                    Save
                  </button>
                  <button
                    onClick={cancelNotesEdit}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <X className="w-3 h-3" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : !isReadOnly ? (
              <div className="flex items-start justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <p className="text-sm text-gray-900">
                    {notes || <span className="text-gray-500 italic">No notes</span>}
                  </p>
                </div>
                <button
                  onClick={() => setIsEditingNotes(true)}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-gray-300 rounded transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
              </div>
            ) : notes ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <p className="text-sm text-gray-900">
                  {notes}
                </p>
              </div>
            ) : null}
          </div>
        </div>
        
        <InputMasker
          sequences={sequences}
          isReadOnly={isReadOnly}
          activeMaskTypes={activeMaskTypes as any}
          maskedResidues={maskedResidues as any}
          onMaskedResiduesChange={(next) => setMaskedResidues(next as any)}
        />
      </div>

      {/* Split View: 3D Structure (left) and Controls/Info (right) - Uses natural height */}
      <div className="flex bg-white flex-1">
        {/* Left Half - Structure Preview using Molecule3DViewer only */}
        <div ref={leftPanelRef} className="w-1/2 p-6 flex flex-col">
          {/* Title */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-light text-gray-900">Structure Preview</h2>
          </div>

          {/* 3D Structure Viewer */}
          <Molecule3DViewer
            pdbContent={pdbContent}
            height="h-[calc(100vh-320px)] min-h-[calc(70vh-224px)]"
            backgroundColor="white"
            showControls={true}
            /* Keep the 3D viewer display-only here; sequence/masking handled above */
            showSequence={false}
            interactive={false}
            highlightedResidues={Array.from(new Set(Array.from(maskedResidues.values()).flatMap(set => Array.from(set))))}
            highlightColor="red"
          />
        </div>

        {/* Right Half - Mask Controls and Info */}
        <div className="w-1/2 border-l border-gray-200 p-6 pb-12 flex flex-col min-h-full">
          {/* Title and Instructions */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-3xl font-light text-gray-900">Masking Control Board</h2>
              {isReadOnly ? (
                <span className="px-4 py-2 bg-gray-100 text-gray-600 text-sm font-medium">
                  Configuration Locked
                </span>
              ) : (
                <button
                  onClick={() => {
                    // Reset all masks
                    setMaskedResidues(new Map([
                      ['sequence', new Set()],
                    ['structure', new Set()],
                    ['sasa', new Set()],
                    ['plddt', new Set()]
                  ]));
                  
                  // Reset all additional data inputs
                  setSasaInput('');
                  setSemanticInput('');
                  setOrthologousInput('');
                  setDomainsInput('');
                  
                  toast.success('All masks and additional data have been reset', { duration: 3000 });
                }}
                  className="px-4 py-2 bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors"
                >
                  Reset All Changes
                </button>
              )}
            </div>
            <p className="text-sm text-gray-600">
              {isReadOnly 
                ? 'This configuration has been submitted to Odyssey and cannot be modified.'
                : 'Click on or drag across residues to mask them with active mask types OR manually add masks to certain residues'
              }
            </p>
          </div>

          {/* Mask Type Controls */}
          <div>
            <div className="flex items-start gap-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-3">Mask Types</h3>
                <div className="flex gap-2">
                {(['sequence', 'structure', 'sasa'] as MaskType[]).map(maskType => {
                  const count = maskedResidues.get(maskType)?.size || 0;
                  return (
                    <div key={maskType} className="text-center">
                      <label className={`flex flex-col items-center gap-1 ${isReadOnly ? '' : 'cursor-pointer hover:bg-gray-50'} p-2 rounded min-w-[85px]`}>
                        <input
                          type="checkbox"
                          checked={activeMaskTypes.has(maskType)}
                          disabled={isReadOnly}
                          onChange={(e) => {
                            if (isReadOnly) return;
                            const newActiveMasks = new Set(activeMaskTypes);
                            if (e.target.checked) {
                              newActiveMasks.add(maskType);
                            } else {
                              newActiveMasks.delete(maskType);
                            }
                            setActiveMaskTypes(newActiveMasks);
                          }}
                          className={`w-5 h-5 rounded border-gray-300 ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'focus:ring-2 focus:ring-offset-0'}`}
                          style={{
                            accentColor: MASK_COLORS[maskType]
                          }}
                        />
                        <span 
                          className="text-sm font-medium capitalize"
                          style={{ color: MASK_COLORS[maskType] }}
                        >
                          {maskType.toUpperCase()}
                        </span>
                        <div className="text-xs text-gray-600 text-center leading-tight">
                          <div className="whitespace-nowrap">{count} residues</div>
                          <div>masked</div>
                        </div>
                      </label>
                    </div>
                  );
                })}
                </div>
              </div>
              
              {/* Manual Mask Input */}
              <div className="flex-1 border-l border-gray-200 pl-4">
                <h4 className="text-base font-semibold text-gray-900 mb-3">Manual Masker</h4>
                <div>
                  <div className="flex gap-2 mb-2">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Chain</label>
                      <select
                        value={manualMaskChain}
                        onChange={(e) => setManualMaskChain(e.target.value)}
                        disabled={isReadOnly}
                        className={`w-14 px-2 py-1.5 bg-white border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        {sequences.map(seq => (
                          <option key={seq.chain} value={seq.chain}>
                            {seq.chain}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm text-gray-600 mb-1">
                        Position
                      </label>
                      <input
                        type="text"
                        value={manualMaskRange}
                        disabled={isReadOnly}
                        onChange={(e) => {
                          if (isReadOnly) return;
                          const value = e.target.value;
                          setManualMaskRange(value);
                          // Validate format: must be number or number-number
                          const isValid = /^\d+$/.test(value) || /^\d+-\d+$/.test(value) || value === '';
                          setManualMaskError(value !== '' && !isValid);
                        }}
                        placeholder="ex) 1-5, 7"
                        className={`w-full px-2 py-1.5 bg-white border text-sm focus:outline-none focus:ring-1 ${
                          manualMaskError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-gray-400'
                        } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                      />
                    </div>
                  </div>
                  <button
                    disabled={isReadOnly}
                    className={`w-full px-4 py-2 bg-gray-900 text-white text-sm font-medium ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-800'} transition-colors`}
                    onClick={() => {
                        if (!manualMaskChain || !manualMaskRange || manualMaskError) {
                          toast.error('Please select a chain and enter a valid range');
                          return;
                        }
                        
                        // Parse input - can be single number or range
                        let start: number, end: number;
                        if (manualMaskRange.includes('-')) {
                          // It's a range
                          const parts = manualMaskRange.split('-').map(Number);
                          start = parts[0];
                          end = parts[1];
                          if (start > end) {
                            toast.error('Start position must be less than end position');
                            return;
                          }
                        } else {
                          // It's a single number
                          start = end = Number(manualMaskRange);
                        }
                        
                        // Find the chain data
                        const chainData = sequences.find(seq => seq.chain === manualMaskChain);
                        if (!chainData) {
                          toast.error('Chain not found');
                          return;
                        }
                        
                        // Validate position is within chain bounds
                        if (start < 1 || end > chainData.residues.length) {
                          toast.error(`Position must be between 1 and ${chainData.residues.length} for chain ${manualMaskChain}`);
                          return;
                        }
                        
                        // Apply masks to the range
                        const newMasks = new Map<MaskType, Set<string>>();
                        maskedResidues.forEach((residues, maskType) => {
                          newMasks.set(maskType, new Set(residues));
                        });
                        
                        // Process each residue in the range
                        const allMaskTypes: MaskType[] = ['sequence', 'structure', 'sasa', 'plddt'];
                        for (let position = start; position <= end; position++) {
                          // Use position index (1-based) instead of PDB residue number
                          const residue = chainData.residues[position - 1]; // Convert to 0-based index
                          if (residue) {
                            const key = `${residue.chain}-${residue.resNum}`; // Use actual PDB resNum for the key
                            
                            // First, remove this residue from ALL mask types
                            allMaskTypes.forEach(maskType => {
                              const maskSet = newMasks.get(maskType) || new Set();
                              maskSet.delete(key);
                              newMasks.set(maskType, maskSet);
                            });
                            
                            // Then add ONLY for active mask types
                            activeMaskTypes.forEach(maskType => {
                              const maskSet = newMasks.get(maskType) || new Set();
                              maskSet.add(key);
                              newMasks.set(maskType, maskSet);
                            });
                          }
                        }
                        
                        setMaskedResidues(newMasks);
                        const successMessage = start === end 
                          ? `Applied masks to residue ${start} in chain ${manualMaskChain}`
                          : `Applied masks to residues ${start}-${end} in chain ${manualMaskChain}`;
                        toast.success(successMessage, { duration: 3000 });
                        
                        // Clear position input only, keep chain selected
                        setManualMaskRange('');
                      }}
                    >
                      Apply
                    </button>
                </div>
                {/* Error message div with reserved space */}
                <div className="h-4">
                  {manualMaskError && (
                    <p className="text-red-500 text-xs mt-1">Invalid format</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Data Options - Show as vertical headings */}
          {(additionalDataOptions.sasa || additionalDataOptions.semanticDescription || 
            additionalDataOptions.orthologousGroups || additionalDataOptions.domains) && (
            <div className="mt-6 space-y-3">
              {additionalDataOptions.sasa && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-base font-semibold text-gray-900">SASA</h3>
                    <button
                      onClick={() => setShowSasaInfo(!showSasaInfo)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="SASA information"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </div>
                  {showSasaInfo && (
                    <p className="text-sm text-gray-600 mb-2">
                      SASA data must be in the form of {totalResidues} real numbers, separated by commas. SASA masked residues (underlined in green) will automatically assume a value of -1, regardless of what value is inputted.
                    </p>
                  )}
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={sasaInput}
                      onChange={(e) => setSasaInput(e.target.value)}
                      placeholder="Enter SASA data..."
                      disabled={isReadOnly}
                      className={`flex-1 px-3 py-2 bg-white border border-gray-300 text-base focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <button
                      onClick={() => {
                        if (!sasaInput.trim()) {
                          toast.error('Please enter SASA data first');
                          return;
                        }
                        
                        const totalResidues = sequences.reduce((total, seq) => total + seq.residues.length, 0);
                        const sasaValues = sasaInput.split(',').map(v => v.trim()).filter(v => v);
                        
                        if (sasaValues.length !== totalResidues) {
                          toast.error(`SASA data must have exactly ${totalResidues} values. You provided ${sasaValues.length}.`);
                          return;
                        }
                        
                        // Validate that all values are numbers
                        for (let i = 0; i < sasaValues.length; i++) {
                          const value = parseFloat(sasaValues[i]);
                          if (isNaN(value)) {
                            toast.error(`SASA data must contain only numbers. Found invalid value: '${sasaValues[i]}'`);
                            return;
                          }
                        }
                        
                        setHasUploadedSasa(true);
                        toast.success('SASA data uploaded successfully', { duration: 3000 });
                      }}
                      disabled={isReadOnly}
                      className={`px-4 py-2 text-white text-base font-medium transition-colors ${isReadOnly ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-900 hover:bg-gray-800'}`}
                    >
                      {hasUploadedSasa ? 'Update Data' : 'Upload Data'}
                    </button>
                  </div>
                </div>
              )}
              {additionalDataOptions.semanticDescription && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-base font-semibold text-gray-900">Semantic Description</h3>
                    <button
                      onClick={() => setShowSemanticInfo(!showSemanticInfo)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Semantic Description information"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </div>
                  {showSemanticInfo && (
                    <p className="text-sm text-gray-600 mb-2">
                      Semantic Description data should be separated by commas.
                    </p>
                  )}
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={semanticInput}
                      onChange={(e) => setSemanticInput(e.target.value)}
                      placeholder="Enter Semantic Description data..."
                      disabled={isReadOnly}
                      className={`flex-1 px-3 py-2 bg-white border border-gray-300 text-base focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <button
                      onClick={() => {
                        if (!semanticInput.trim()) {
                          toast.error('Please enter semantic description first');
                          return;
                        }
                        setHasUploadedSemantic(true);
                        toast.success('Semantic description uploaded successfully', { duration: 3000 });
                      }}
                      disabled={isReadOnly}
                      className={`px-4 py-2 text-white text-base font-medium transition-colors ${isReadOnly ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-900 hover:bg-gray-800'}`}
                    >
                      {hasUploadedSemantic ? 'Update Data' : 'Upload Data'}
                    </button>
                  </div>
                </div>
              )}
              {additionalDataOptions.orthologousGroups && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-base font-semibold text-gray-900">Orthologous Groups</h3>
                    <button
                      onClick={() => setShowOrthologousInfo(!showOrthologousInfo)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Orthologous Groups information"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </div>
                  {showOrthologousInfo && (
                    <p className="text-sm text-gray-600 mb-2">
                      Orthologous Groups data should be separated by commas.
                    </p>
                  )}
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={orthologousInput}
                      onChange={(e) => setOrthologousInput(e.target.value)}
                      placeholder="Enter Orthologous Groups data..."
                      disabled={isReadOnly}
                      className={`flex-1 px-3 py-2 bg-white border border-gray-300 text-base focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <button
                      onClick={() => {
                        if (!orthologousInput.trim()) {
                          toast.error('Please enter orthologous groups data first');
                          return;
                        }
                        setHasUploadedOrthologous(true);
                        toast.success('Orthologous groups uploaded successfully', { duration: 3000 });
                      }}
                      disabled={isReadOnly}
                      className={`px-4 py-2 text-white text-base font-medium transition-colors ${isReadOnly ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-900 hover:bg-gray-800'}`}
                    >
                      {hasUploadedOrthologous ? 'Update Data' : 'Upload Data'}
                    </button>
                  </div>
                </div>
              )}
              {additionalDataOptions.domains && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-base font-semibold text-gray-900">Domains</h3>
                    <button
                      onClick={() => setShowDomainsInfo(!showDomainsInfo)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Domains information"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </div>
                  {showDomainsInfo && (
                    <p className="text-sm text-gray-600 mb-2">
                      Domain data should be in the form of {totalResidues} domains, separated by commas. If the domain is empty, input NULL.
                    </p>
                  )}
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={domainsInput}
                      onChange={(e) => setDomainsInput(e.target.value)}
                      placeholder="Enter Domains data..."
                      disabled={isReadOnly}
                      className={`flex-1 px-3 py-2 bg-white border border-gray-300 text-base focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <button
                      onClick={() => {
                        if (!domainsInput.trim()) {
                          toast.error('Please enter domains data first');
                          return;
                        }
                        
                        const totalResidues = sequences.reduce((total, seq) => total + seq.residues.length, 0);
                        const domainValues = domainsInput.split(',').map(v => v.trim());
                        
                        if (domainValues.length !== totalResidues) {
                          toast.error(`Domains data must have exactly ${totalResidues} entries. You provided ${domainValues.length}.`);
                          return;
                        }
                        
                        setHasUploadedDomains(true);
                        toast.success('Domains data uploaded successfully', { duration: 3000 });
                      }}
                      disabled={isReadOnly}
                      className={`px-4 py-2 text-white text-base font-medium transition-colors ${isReadOnly ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-900 hover:bg-gray-800'}`}
                    >
                      {hasUploadedDomains ? 'Update Data' : 'Upload Data'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Generations Selector */}
          {!isReadOnly && (
            <div className="mt-6 flex items-start gap-4">
              <label className="text-base font-semibold text-gray-900 mt-1.5">
                Generations:
              </label>
              <div className="flex-1">
                {(() => {
                  const options = [1, 2, 4, 12, 24, 48, 96, 192] as const;
                  const idx = Math.max(0, options.indexOf(numGens as any));
                  const pct = (idx / (options.length - 1)) * 100;
                  return (
                    <div
                      className="relative"
                      style={{ ['--thumb' as any]: '20px', ['--track' as any]: '10px' }}
                    >
                      {/* Tick marks overlay, positioned at exact slider positions */}
                      <div className="pointer-events-none absolute left-0 right-0 top-0 bottom-0 z-0">
                        {options.map((value, index) => (
                          <div
                            key={`tracktick-${value}`}
                            className="absolute top-0 bottom-0 flex items-center"
                            style={{
                              left: `${(index / (options.length - 1)) * 100}%`,
                              transform: 'translateX(-50%)'
                            }}
                          >
                            <span className="block w-px h-full" style={{ background: '#E5E7EB' }} />
                          </div>
                        ))}
                      </div>
                      <style>
                        {`
                          .generations-slider {
                            -webkit-appearance: none;
                            appearance: none;
                            outline: none;
                            margin: 0;
                            padding: 0;
                            width: calc(100% + var(--thumb));
                            margin-left: calc(var(--thumb) / -2);
                            margin-right: calc(var(--thumb) / -2);
                            height: var(--track);
                            border-radius: 9999px;
                            cursor: pointer;
                          }

                          .generations-slider::-webkit-slider-thumb {
                            -webkit-appearance: none;
                            appearance: none;
                            width: var(--thumb);
                            height: var(--thumb);
                            border-radius: 50%;
                            background: #6B7280;
                            cursor: pointer;
                            border: none;
                            box-shadow: none;
                            margin-top: calc((var(--track) - var(--thumb)) / 2);
                          }

                          .generations-slider::-webkit-slider-runnable-track {
                            width: 100%;
                            height: var(--track);
                            border-radius: 9999px;
                            background: transparent;
                            border: none;
                            box-shadow: none;
                          }

                          .generations-slider::-moz-range-thumb {
                            width: var(--thumb);
                            height: var(--thumb);
                            border-radius: 50%;
                            background: #6B7280;
                            cursor: pointer;
                            border: none;
                            box-shadow: none;
                            margin: 0;
                          }

                          .generations-slider::-moz-range-track {
                            width: 100%;
                            height: var(--track);
                            border-radius: 9999px;
                            background: transparent;
                            border: none;
                          }

                          .generations-slider::-moz-range-progress {
                            background: transparent;
                          }
                        `}
                      </style>
                      <input
                        type="range"
                        min={0}
                        max={options.length - 1}
                        step={1}
                        value={idx}
                        onChange={(e) => {
                          const nextIdx = parseInt(e.target.value);
                          setNumGens(options[nextIdx]);
                        }}
                        className="generations-slider relative z-10"
                        style={{
                          background: `linear-gradient(to right, #6B7280 0%, #6B7280 ${pct}%, #E5E7EB ${pct}%, #E5E7EB 100%)`
                        }}
                      />
                      {/* Labels */}
                      <div className="relative text-xs text-gray-500 mt-1">
                        {options.map((value, index) => (
                          <div
                            key={value}
                            className="absolute select-none"
                            style={{
                              left: `${(index / (options.length - 1)) * 100}%`,
                              transform: 'translateX(-50%)'
                            }}
                          >
                            {value}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Action Buttons and Counter */}
          <div className="mt-6 flex items-center gap-3">
            {/* Add Data Button */}
            <button
              onClick={() => {
                setShowAddDataModal(true);
              }}
              className="flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-base font-medium hover:bg-gray-50 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Data
            </button>
            
            {/* Total Residues Counter */}
            <div className="flex-1 text-center">
              <span className="text-xl font-semibold text-gray-900">
                {(() => {
                  // Calculate total unique residues with at least one mask
                  const allMaskedKeys = new Set<string>();
                  maskedResidues.forEach((residues) => {
                    residues.forEach(key => allMaskedKeys.add(key));
                  });
                  return allMaskedKeys.size;
                })()}
              </span>
              <span className="ml-1 text-base text-gray-600">Total Residues Masked</span>
            </div>
            
            <div className="flex flex-col gap-2 items-stretch">
              {/* Run Odyssey Pipeline Button or Status */}
              {isReadOnly ? (
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 text-base font-medium">
                  <Info className="h-4 w-4" />
                  Configuration Submitted to Odyssey
                </div>
              ) : (
              <button
                ref={runOdysseyButtonRef}
                onClick={async () => {
                try {
                  setOdysseyLoading(true);
                  
                  // Calculate total residues for SASA array
                  const totalResidues = sequences.reduce((total, seq) => total + seq.residues.length, 0);
                  
                  // Parse SASA input - default to -1.0 when no value supplied
                  let sasaArray: number[] = [];
                  for (let i = 0; i < totalResidues; i++) {
                    sasaArray.push(-1.0);
                  }
                  
                  if (sasaInput.trim()) {
                    const sasaValues = sasaInput.split(',').map(v => parseFloat(v.trim()));
                    
                    // Get SASA masked residue positions
                    const sasaMaskedSet = maskedResidues.get('sasa') || new Set();
                    
                    // Build SASA array with proper indexing
                    let globalIndex = 0;
                    sequences.forEach(seq => {
                      seq.residues.forEach(residue => {
                        const key = `${residue.chain}-${residue.resNum}`;
                        
                        // If this residue is SASA-masked, keep it as -1
                        if (sasaMaskedSet.has(key)) {
                          sasaArray[globalIndex] = -1.0;
                        } else if (globalIndex < sasaValues.length && !isNaN(sasaValues[globalIndex])) {
                          // Use the provided value if available and valid
                          sasaArray[globalIndex] = sasaValues[globalIndex];
                        }
                        // Otherwise keeps default of -1.0
                        
                        globalIndex++;
                      });
                    });
                  } else {
                    // If no input provided, still need to check for SASA masks
                    const sasaMaskedSet = maskedResidues.get('sasa') || new Set();
                    let globalIndex = 0;
                    sequences.forEach(seq => {
                      seq.residues.forEach(residue => {
                        const key = `${residue.chain}-${residue.resNum}`;
                        if (sasaMaskedSet.has(key)) {
                          sasaArray[globalIndex] = -1.0;
                        }
                        globalIndex++;
                      });
                    });
                  }
                  
                  // Build full sequence string with sequence masks applied
                  const sequenceMaskedSet = maskedResidues.get('sequence') || new Set();
                  let maskedSequence = '';
                  sequences.forEach(seq => {
                    seq.residues.forEach(residue => {
                      const key = `${residue.chain}-${residue.resNum}`;
                      if (sequenceMaskedSet.has(key)) {
                        maskedSequence += '_';
                      } else {
                        maskedSequence += residue.resName;
                      }
                    });
                  });
                  
                  // Process orthologous groups - remove spaces after commas
                  const processedOrthologousGroups = orthologousInput.trim()
                    .split(',')
                    .map(group => group.trim())
                    .join(',');
                  
                  // Process domains input
                  let domainsArray: string[][] = [];
                  for (let i = 0; i < totalResidues; i++) {
                    domainsArray.push([]);
                  }
                  
                  if (domainsInput.trim()) {
                    const domainValues = domainsInput.split(',').map(v => v.trim());
                    
                    for (let i = 0; i < Math.min(domainValues.length, totalResidues); i++) {
                      const domainValue = domainValues[i];
                      // Check if the value is NULL (case-insensitive)
                      if (domainValue && domainValue.toUpperCase() !== 'NULL') {
                        // If not NULL and not empty, add it as a single-element array
                        domainsArray[i] = [domainValue];
                      }
                      // Otherwise keep it as empty array (already initialized)
                    }
                  }
                  
                  // Note: JSON generation is now handled by gemmi backend
                  // Mask data is already stored in molecule_viewer table

                  // Generate a unique job ID
                  const jobId = `odyssey-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
                  
                  // Get current user
                  const { data: { user } } = await supabase.auth.getUser();
                  if (!user) {
                    toast.error('Authentication required');
                    setOdysseyLoading(false);
                    return;
                  }
                  
                  // Get project ID from session storage
                  const projectId = sessionStorage.getItem('currentProjectId');
                  
                  // First check if this molecule has already been run through Odyssey
                  const { data: currentMolecule, error: fetchError } = await supabase
                    .from('molecule_viewer')
                    .select('ran_odyssey')
                    .eq('id', moleculeId)
                    .single();
                  
                  if (fetchError) {
                    console.error('Error fetching molecule status:', fetchError);
                    toast.error('Failed to check molecule status');
                    setOdysseyLoading(false);
                    return;
                  }
                  
                  if (currentMolecule?.ran_odyssey) {
                    console.warn('Molecule already submitted to Odyssey');
                    toast.error('This configuration has already been submitted to Odyssey');
                    setOdysseyLoading(false);
                    setIsReadOnly(true); // Update UI to reflect read-only state
                    return;
                  }
                  
                  // First create the odyssey_jobs record (must exist before we can reference it)
                  console.log('Creating odyssey_jobs record with job_id:', jobId);
                  const { error: dbError } = await supabase
                    .from('odyssey_jobs')
                    .insert({
                      job_id: jobId,
                      user_id: user.id,
                      project_id: projectId || null,
                      name: runName.trim() || 'Design',  // Use custom run name or simple default
                      pdb_content: pdbContent,  // Store PDB content for backend processing
                      num_gens: numGens, // Persist generations immediately to avoid race with trigger
                      status: 'pending',
                      current_stage: 'queued',
                      stage_details: 'Job queued for processing',
                      workflow_type: 'enzyme',  // Default to enzyme type
                      generation_id: null  // Pass null for now, will implement generation tracking later
                    });
                  
                  if (dbError) {
                    console.error('Failed to create odyssey job in database:', dbError.message || JSON.stringify(dbError));
                    toast.error(`Failed to create job: ${dbError.message || 'Database error'}`);
                    setOdysseyLoading(false);
                    return;
                  }
                  
                  console.log('Successfully created odyssey_jobs record');
                  
                  // Get mask sets for storing in molecule_viewer (reuse existing variables)
                  // sequenceMaskedSet already declared above
                  const structureMaskedSet = maskedResidues.get('structure') || new Set();
                  
                  // Now update molecule_viewer with mask data and reference to odyssey job
                  const updateData = {
                    sequence_masks: JSON.stringify(Array.from(sequenceMaskedSet)),
                    structure_masks: JSON.stringify(Array.from(structureMaskedSet)),
                    sasa_masks: sasaInput.trim() ? JSON.stringify(Array.from(maskedResidues.get('sasa') || new Set())) : null,
                    plddt_masks: JSON.stringify(Array.from(maskedResidues.get('plddt') || new Set())),
                    sasa: sasaInput.trim() || null,
                    semantic_description: semanticInput.trim() || null,
                    orthologous_groups: orthologousInput.trim() || null,
                    domains: domainsInput.trim() || null,
                    ran_odyssey: true,
                    odyssey_job_id: jobId
                  };
                  
                  console.log('Attempting to update molecule_viewer with:', updateData);
                  
                  const { data: updateResult, error: updateError } = await supabase
                    .from('molecule_viewer')
                    .update(updateData)
                    .eq('id', moleculeId)
                    .select();
                  
                  if (updateError) {
                    console.error('Error updating molecule_viewer:', updateError);
                    console.error('Update error details:', {
                      code: updateError.code,
                      message: updateError.message,
                      details: updateError.details,
                      hint: updateError.hint
                    });
                    
                    // Check for specific error types
                    if (updateError.code === '23503') { // Foreign key constraint violation
                      toast.error('Failed to link configuration to Odyssey job. Cannot start pipeline without saving configuration.');
                    } else if (updateError.code === '23505') { // Unique constraint violation
                      toast.error('This Odyssey job ID already exists. Please try again.');
                    } else if (updateError.code === '42501') { // Insufficient privilege
                      toast.error('You do not have permission to update this molecule.');
                    } else {
                      toast.error(`Failed to save configuration: ${updateError.message}`);
                    }
                    
                    // CRITICAL: Don't continue with pipeline if mask data wasn't saved
                    setOdysseyLoading(false);
                    return;
                  }
                  
                  console.log('Successfully updated molecule_viewer:', updateResult);
                  setIsReadOnly(true); // Update UI to reflect read-only state
                  
                  toast.info('Triggering Odyssey pipeline...');
                  
                  // Check if this is a next generation (has previous_generation field)
                  let baseJobId: string | null = null;
                  if (moleculeData?.previous_generation) {
                    // Find the parent molecule's odyssey_job_id
                    const { data: parentMolecule } = await supabase
                      .from('molecule_viewer')
                      .select('odyssey_job_id')
                      .eq('id', moleculeData.previous_generation)
                      .single();
                    
                    if (parentMolecule?.odyssey_job_id) {
                      baseJobId = parentMolecule.odyssey_job_id;
                      console.log('Found parent job ID:', baseJobId);
                    }
                  }
                  
                  // Get user session token for authentication
                  const { data: { session } } = await supabase.auth.getSession();
                  if (!session) {
                    throw new Error('User not authenticated');
                  }

                  // Trigger the Odyssey pipeline
                  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/trigger-odyssey`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${session.access_token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      jobId: jobId,
                      pdbContent: pdbContent,  // Send raw PDB content instead of JSON
                      name: moleculeData?.name || runName || 'Design',
                      baseJobId: baseJobId,  // Pass the parent job ID if this is a next generation
                      numGens: numGens  // Pass the number of generations
                    })
                  });
                  
                  if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`Pipeline failed: ${response.statusText} - ${errorText}`);
                  }
                  
                  toast.success('Odyssey pipeline started successfully!', { duration: 3000 });
                  toast.info('Analysis takes 10-20 minutes. You\'ll see results update in real-time!', { duration: 6000 });
                  
                  // Open Odyssey job in new tab
                  setTimeout(() => {
                    openOdysseyTab(jobId, runName.trim() || 'Design');
                  }, 1500);
                  
                } catch (error) {
                  console.error('Error triggering Odyssey pipeline:', error);
                  toast.error(`Failed to trigger Odyssey pipeline: ${error instanceof Error ? error.message : String(error)}`);
                } finally {
                  setOdysseyLoading(false);
                }
              }}
              disabled={odysseyLoading}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-800 text-white text-base font-medium hover:from-purple-700 hover:to-purple-900 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Rocket className="h-4 w-4" />
              {odysseyLoading ? 'Starting Odyssey Pipeline...' : 'Run Odyssey Pipeline'}
            </button>
              )}
              <button
                onClick={handleDownloadInputJson}
                className="px-4 py-2 bg-white border border-purple-200 text-purple-700 text-base font-medium hover:bg-purple-50 transition-colors"
              >
                Create Input JSON
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Add Data Modal */}
      {showAddDataModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 w-96 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Additional Data</h3>
            
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2">
                <input
                  type="checkbox"
                  checked={additionalDataOptions.sasa}
                  onChange={(e) => setAdditionalDataOptions(prev => ({ ...prev, sasa: e.target.checked }))}
                  className="w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500"
                />
                <span className="text-base text-gray-700">SASA</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2">
                <input
                  type="checkbox"
                  checked={additionalDataOptions.semanticDescription}
                  onChange={(e) => setAdditionalDataOptions(prev => ({ ...prev, semanticDescription: e.target.checked }))}
                  className="w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500"
                />
                <span className="text-base text-gray-700">Semantic Description</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2">
                <input
                  type="checkbox"
                  checked={additionalDataOptions.orthologousGroups}
                  onChange={(e) => setAdditionalDataOptions(prev => ({ ...prev, orthologousGroups: e.target.checked }))}
                  className="w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500"
                />
                <span className="text-base text-gray-700">Orthologous Groups</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2">
                <input
                  type="checkbox"
                  checked={additionalDataOptions.domains}
                  onChange={(e) => setAdditionalDataOptions(prev => ({ ...prev, domains: e.target.checked }))}
                  className="w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500"
                />
                <span className="text-base text-gray-700">Domains</span>
              </label>
            </div>
            
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => {
                  // Close modal - the state is already being updated in real-time
                  setShowAddDataModal(false);
                }}
                className="px-4 py-2 bg-gray-900 text-white text-base font-medium hover:bg-gray-800 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
